export type BackupStatus = 'success' | 'in_progress' | 'failed' | 'pending';

export type BackupType = 'automatic' | 'manual';

export interface BackupEntry {
  id: string;
  type: BackupType;
  status: BackupStatus;
  name: string;
  size: number;
  sizeLabel: string;
  date: string;
  server: string;
  duration: number;
  durationLabel: string;
  result: string;
  database: string;
  tablesCount: number;
  filesIncluded: string[];
  checksum: string;
  initiatedBy: string;
  compression: string;
  encryption: boolean;
}

export interface BackupSchedule {
  id: string;
  name: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom';
  cronExpression: string;
  time: string;
  server: string;
  database: string;
  retentionDays: number;
  enabled: boolean;
  lastRun: string | null;
  nextRun: string;
  description: string;
}

export interface StorageInfo {
  totalGB: number;
  usedGB: number;
  availableGB: number;
  usagePercent: number;
  lastUpdated: string;
}

export const statusMeta: Record<BackupStatus, { label: string; color: string; bg: string; dot: string; icon: string }> = {
  success: {
    label: 'Correcto',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
    icon: 'ri-checkbox-circle-line',
  },
  in_progress: {
    label: 'En proceso',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    dot: 'bg-amber-500',
    icon: 'ri-loader-4-line',
  },
  failed: {
    label: 'Fallido',
    color: 'text-red-600',
    bg: 'bg-red-50',
    dot: 'bg-red-500',
    icon: 'ri-close-circle-line',
  },
  pending: {
    label: 'Pendiente',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    dot: 'bg-sky-500',
    icon: 'ri-time-line',
  },
};

export const typeMeta: Record<BackupType, { label: string; icon: string }> = {
  automatic: { label: 'Automático', icon: 'ri-refresh-line' },
  manual: { label: 'Manual', icon: 'ri-user-line' },
};

export const serverOptions = [
  'us-east-1',
  'us-west-2',
  'eu-west-1',
  'sa-east-1',
  'ap-southeast-1',
];

export const databaseOptions = [
  'produccion_principal',
  'analytics_db',
  'logs_db',
  'usuarios_db',
  'configuracion_db',
  'marketplace_db',
];

export const storageInfo: StorageInfo = {
  totalGB: 2048,
  usedGB: 1456.8,
  availableGB: 591.2,
  usagePercent: 71.1,
  lastUpdated: '2026-07-12T17:45:00Z',
};

export const backupEntries: BackupEntry[] = [
  {
    id: 'BCK-001',
    type: 'automatic',
    status: 'success',
    name: 'Backup Diario - producción_principal',
    size: 4.52,
    sizeLabel: '4.52 GB',
    date: '2026-07-12T04:00:00Z',
    server: 'us-east-1',
    duration: 3.2,
    durationLabel: '3 min 12 s',
    result: 'Backup completado exitosamente. 482 tablas respaldadas. Checksum verificado.',
    database: 'produccion_principal',
    tablesCount: 482,
    filesIncluded: ['schema.sql', 'data_dump.sql.gz', 'functions.sql', 'triggers.sql', 'indexes.sql'],
    checksum: 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-002',
    type: 'automatic',
    status: 'success',
    name: 'Backup Diario - analytics_db',
    size: 2.18,
    sizeLabel: '2.18 GB',
    date: '2026-07-12T04:15:00Z',
    server: 'us-east-1',
    duration: 2.1,
    durationLabel: '2 min 06 s',
    result: 'Backup completado exitosamente. 156 tablas respaldadas.',
    database: 'analytics_db',
    tablesCount: 156,
    filesIncluded: ['schema.sql', 'data_dump.sql.gz', 'functions.sql'],
    checksum: 'b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-003',
    type: 'automatic',
    status: 'success',
    name: 'Backup Diario - logs_db',
    size: 6.84,
    sizeLabel: '6.84 GB',
    date: '2026-07-12T04:30:00Z',
    server: 'us-east-1',
    duration: 5.8,
    durationLabel: '5 min 48 s',
    result: 'Backup completado exitosamente. 89 tablas respaldadas. Rotación de logs aplicada.',
    database: 'logs_db',
    tablesCount: 89,
    filesIncluded: ['schema.sql', 'data_dump.sql.gz'],
    checksum: 'c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-004',
    type: 'automatic',
    status: 'success',
    name: 'Backup Diario - usuarios_db',
    size: 1.35,
    sizeLabel: '1.35 GB',
    date: '2026-07-12T04:45:00Z',
    server: 'us-west-2',
    duration: 1.4,
    durationLabel: '1 min 24 s',
    result: 'Backup completado exitosamente. 234 tablas respaldadas.',
    database: 'usuarios_db',
    tablesCount: 234,
    filesIncluded: ['schema.sql', 'data_dump.sql.gz', 'functions.sql', 'triggers.sql'],
    checksum: 'd4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-005',
    type: 'automatic',
    status: 'in_progress',
    name: 'Backup Semanal - Full System',
    size: 12.30,
    sizeLabel: '12.30 GB',
    date: '2026-07-12T17:00:00Z',
    server: 'sa-east-1',
    duration: 18.5,
    durationLabel: '18 min 30 s',
    result: 'Backup en progreso. 6/9 bases de datos completadas. ETA: 7 minutos.',
    database: 'todas',
    tablesCount: 1245,
    filesIncluded: ['full_schema.sql', 'full_data_dump.sql.gz', 'all_functions.sql', 'all_triggers.sql', 'configuration.tar.gz'],
    checksum: '—',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-006',
    type: 'manual',
    status: 'success',
    name: 'Backup Manual - Pre-Deploy v4.2.0',
    size: 8.91,
    sizeLabel: '8.91 GB',
    date: '2026-07-11T22:30:00Z',
    server: 'us-east-1',
    duration: 7.2,
    durationLabel: '7 min 12 s',
    result: 'Backup pre-deploy completado. Snapshot del sistema antes de actualización a v4.2.0.',
    database: 'produccion_principal',
    tablesCount: 482,
    filesIncluded: ['pre_deploy_schema.sql', 'pre_deploy_data.sql.gz', 'config_backup.json'],
    checksum: 'e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0',
    initiatedBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-007',
    type: 'automatic',
    status: 'success',
    name: 'Backup Diario - configuracion_db',
    size: 0.42,
    sizeLabel: '0.42 GB',
    date: '2026-07-12T04:00:00Z',
    server: 'eu-west-1',
    duration: 0.8,
    durationLabel: '48 s',
    result: 'Backup completado exitosamente. 47 tablas respaldadas.',
    database: 'configuracion_db',
    tablesCount: 47,
    filesIncluded: ['schema.sql', 'data_dump.sql.gz'],
    checksum: 'f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-008',
    type: 'automatic',
    status: 'failed',
    name: 'Backup Diario - marketplace_db',
    size: 0,
    sizeLabel: '—',
    date: '2026-07-12T04:00:00Z',
    server: 'ap-southeast-1',
    duration: 2.5,
    durationLabel: '2 min 30 s',
    result: 'ERROR: Connection timeout al conectar con el servidor ap-southeast-1. Reintentos agotados (3/3).',
    database: 'marketplace_db',
    tablesCount: 0,
    filesIncluded: [],
    checksum: '—',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-009',
    type: 'manual',
    status: 'success',
    name: 'Backup Manual - Migración Schema v3',
    size: 3.67,
    sizeLabel: '3.67 GB',
    date: '2026-07-11T15:45:00Z',
    server: 'eu-west-1',
    duration: 4.1,
    durationLabel: '4 min 06 s',
    result: 'Backup pre-migración completado. Esquema de base de datos respaldado antes de migración.',
    database: 'produccion_principal',
    tablesCount: 482,
    filesIncluded: ['pre_migration_schema.sql', 'pre_migration_data.sql.gz'],
    checksum: 'a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2',
    initiatedBy: 'Laura Ramírez (laura.ramirez@readdy.ai)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-010',
    type: 'manual',
    status: 'success',
    name: 'Backup Manual - Auditoría Trimestral',
    size: 15.42,
    sizeLabel: '15.42 GB',
    date: '2026-07-10T10:00:00Z',
    server: 'sa-east-1',
    duration: 14.3,
    durationLabel: '14 min 18 s',
    result: 'Backup de auditoría completado. Todas las bases de datos respaldadas para cumplimiento normativo.',
    database: 'todas',
    tablesCount: 1245,
    filesIncluded: ['audit_full_schema.sql', 'audit_full_data.sql.gz', 'audit_report.json', 'compliance_checklist.pdf'],
    checksum: 'b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3',
    initiatedBy: 'Ana Torres (ana.torres@readdy.ai)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-011',
    type: 'automatic',
    status: 'success',
    name: 'Backup Semanal - analytics_db',
    size: 5.76,
    sizeLabel: '5.76 GB',
    date: '2026-07-11T04:00:00Z',
    server: 'us-east-1',
    duration: 4.5,
    durationLabel: '4 min 30 s',
    result: 'Backup semanal completado exitosamente. Datos agregados y particionados correctamente.',
    database: 'analytics_db',
    tablesCount: 156,
    filesIncluded: ['schema.sql', 'data_dump.sql.gz', 'functions.sql', 'materialized_views.sql'],
    checksum: 'c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-012',
    type: 'automatic',
    status: 'failed',
    name: 'Backup Diario - logs_db',
    size: 0,
    sizeLabel: '—',
    date: '2026-07-11T04:30:00Z',
    server: 'us-east-1',
    duration: 1.2,
    durationLabel: '1 min 12 s',
    result: 'ERROR: Espacio insuficiente en disco. Requerido: 7.2 GB. Disponible: 2.1 GB. Backup abortado.',
    database: 'logs_db',
    tablesCount: 0,
    filesIncluded: [],
    checksum: '—',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-013',
    type: 'manual',
    status: 'success',
    name: 'Backup Manual - Hotfix #847',
    size: 2.15,
    sizeLabel: '2.15 GB',
    date: '2026-07-10T23:15:00Z',
    server: 'us-west-2',
    duration: 2.8,
    durationLabel: '2 min 48 s',
    result: 'Backup pre-hotfix completado. Snapshot de producción antes de aplicar parche de seguridad crítico.',
    database: 'produccion_principal',
    tablesCount: 482,
    filesIncluded: ['pre_hotfix_schema.sql', 'pre_hotfix_data.sql.gz', 'hotfix_rollback_plan.txt'],
    checksum: 'd0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5',
    initiatedBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-014',
    type: 'automatic',
    status: 'success',
    name: 'Backup Mensual - Full Archive',
    size: 28.45,
    sizeLabel: '28.45 GB',
    date: '2026-07-01T02:00:00Z',
    server: 'eu-west-1',
    duration: 22.7,
    durationLabel: '22 min 42 s',
    result: 'Backup mensual completado. Archivo completo del sistema. Almacenado en cold storage (Glacier).',
    database: 'todas',
    tablesCount: 1245,
    filesIncluded: ['full_archive_schema.sql', 'full_archive_data.sql.gz', 'full_archive_config.tar.gz', 'full_archive_media.tar.gz'],
    checksum: 'e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-015',
    type: 'manual',
    status: 'success',
    name: 'Backup Manual - Test Restauración',
    size: 1.82,
    sizeLabel: '1.82 GB',
    date: '2026-07-09T16:00:00Z',
    server: 'sa-east-1',
    duration: 3.5,
    durationLabel: '3 min 30 s',
    result: 'Backup de prueba completado. Utilizado para validar procedimiento de restauración en staging.',
    database: 'configuracion_db',
    tablesCount: 47,
    filesIncluded: ['test_restore_schema.sql', 'test_restore_data.sql.gz'],
    checksum: 'f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7',
    initiatedBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-016',
    type: 'automatic',
    status: 'pending',
    name: 'Backup Diario - marketplace_db',
    size: 0,
    sizeLabel: '—',
    date: '2026-07-13T04:00:00Z',
    server: 'ap-southeast-1',
    duration: 0,
    durationLabel: '—',
    result: 'Backup programado para las 04:00 UTC del 13/07/2026.',
    database: 'marketplace_db',
    tablesCount: 0,
    filesIncluded: [],
    checksum: '—',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-017',
    type: 'automatic',
    status: 'pending',
    name: 'Backup Semanal - usuarios_db',
    size: 0,
    sizeLabel: '—',
    date: '2026-07-13T04:30:00Z',
    server: 'us-west-2',
    duration: 0,
    durationLabel: '—',
    result: 'Backup programado para las 04:30 UTC del 13/07/2026.',
    database: 'usuarios_db',
    tablesCount: 0,
    filesIncluded: [],
    checksum: '—',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-018',
    type: 'manual',
    status: 'failed',
    name: 'Backup Manual - Prueba Conexión',
    size: 0,
    sizeLabel: '—',
    date: '2026-07-09T09:20:00Z',
    server: 'ap-southeast-1',
    duration: 0.9,
    durationLabel: '54 s',
    result: 'ERROR: Autenticación fallida en servidor de storage. Token de acceso expirado. Verificar IAM roles.',
    database: 'marketplace_db',
    tablesCount: 0,
    filesIncluded: [],
    checksum: '—',
    initiatedBy: 'Laura Ramírez (laura.ramirez@readdy.ai)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-019',
    type: 'automatic',
    status: 'success',
    name: 'Backup Diario - producción_principal',
    size: 4.48,
    sizeLabel: '4.48 GB',
    date: '2026-07-11T04:00:00Z',
    server: 'us-east-1',
    duration: 3.0,
    durationLabel: '3 min 00 s',
    result: 'Backup completado exitosamente. 480 tablas respaldadas.',
    database: 'produccion_principal',
    tablesCount: 480,
    filesIncluded: ['schema.sql', 'data_dump.sql.gz', 'functions.sql', 'triggers.sql', 'indexes.sql'],
    checksum: 'a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
  {
    id: 'BCK-020',
    type: 'automatic',
    status: 'success',
    name: 'Backup Diario - producción_principal',
    size: 4.41,
    sizeLabel: '4.41 GB',
    date: '2026-07-10T04:00:00Z',
    server: 'us-east-1',
    duration: 3.1,
    durationLabel: '3 min 06 s',
    result: 'Backup completado exitosamente. 479 tablas respaldadas.',
    database: 'produccion_principal',
    tablesCount: 479,
    filesIncluded: ['schema.sql', 'data_dump.sql.gz', 'functions.sql', 'triggers.sql', 'indexes.sql'],
    checksum: 'b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9',
    initiatedBy: 'Sistema (Cron Job)',
    compression: 'gzip',
    encryption: true,
  },
];

export const backupSchedules: BackupSchedule[] = [
  {
    id: 'SCH-001',
    name: 'Backup Diario - Producción Principal',
    frequency: 'daily',
    cronExpression: '0 4 * * *',
    time: '04:00 UTC',
    server: 'us-east-1',
    database: 'produccion_principal',
    retentionDays: 30,
    enabled: true,
    lastRun: '2026-07-12T04:00:00Z',
    nextRun: '2026-07-13T04:00:00Z',
    description: 'Backup diario completo de la base de datos principal de producción. Incluye esquema, datos, funciones y triggers.',
  },
  {
    id: 'SCH-002',
    name: 'Backup Diario - Analytics',
    frequency: 'daily',
    cronExpression: '0 4 * * *',
    time: '04:15 UTC',
    server: 'us-east-1',
    database: 'analytics_db',
    retentionDays: 14,
    enabled: true,
    lastRun: '2026-07-12T04:15:00Z',
    nextRun: '2026-07-13T04:15:00Z',
    description: 'Backup diario de la base de datos de analytics. Datos agregados de escuchas, usuarios y métricas.',
  },
  {
    id: 'SCH-003',
    name: 'Backup Diario - Logs',
    frequency: 'daily',
    cronExpression: '0 4 * * *',
    time: '04:30 UTC',
    server: 'us-east-1',
    database: 'logs_db',
    retentionDays: 7,
    enabled: true,
    lastRun: '2026-07-12T04:30:00Z',
    nextRun: '2026-07-13T04:30:00Z',
    description: 'Backup diario de logs del sistema. Con rotación automática. Logs de más de 7 días se purgan automáticamente.',
  },
  {
    id: 'SCH-004',
    name: 'Backup Semanal - Full System',
    frequency: 'weekly',
    cronExpression: '0 17 * * 0',
    time: 'Domingo 17:00 UTC',
    server: 'sa-east-1',
    database: 'todas',
    retentionDays: 90,
    enabled: true,
    lastRun: '2026-07-12T17:00:00Z',
    nextRun: '2026-07-19T17:00:00Z',
    description: 'Backup semanal completo de todas las bases de datos. Backup de contingencia con retención extendida a 90 días.',
  },
  {
    id: 'SCH-005',
    name: 'Backup Diario - Usuarios',
    frequency: 'daily',
    cronExpression: '0 4 * * *',
    time: '04:45 UTC',
    server: 'us-west-2',
    database: 'usuarios_db',
    retentionDays: 30,
    enabled: true,
    lastRun: '2026-07-12T04:45:00Z',
    nextRun: '2026-07-13T04:45:00Z',
    description: 'Backup diario de la base de datos de usuarios. Perfiles, roles, permisos y datos de autenticación.',
  },
  {
    id: 'SCH-006',
    name: 'Backup Mensual - Archive',
    frequency: 'monthly',
    cronExpression: '0 2 1 * *',
    time: 'Día 1 del mes, 02:00 UTC',
    server: 'eu-west-1',
    database: 'todas',
    retentionDays: 365,
    enabled: true,
    lastRun: '2026-07-01T02:00:00Z',
    nextRun: '2026-08-01T02:00:00Z',
    description: 'Backup de archivo mensual. Cold storage en AWS Glacier. Retención de 1 año. Para cumplimiento normativo y auditorías.',
  },
  {
    id: 'SCH-007',
    name: 'Backup Diario - Configuración',
    frequency: 'daily',
    cronExpression: '0 4 * * *',
    time: '04:00 UTC',
    server: 'eu-west-1',
    database: 'configuracion_db',
    retentionDays: 30,
    enabled: true,
    lastRun: '2026-07-12T04:00:00Z',
    nextRun: '2026-07-13T04:00:00Z',
    description: 'Backup diario de la base de datos de configuración del sistema.',
  },
  {
    id: 'SCH-008',
    name: 'Backup Diario - Marketplace',
    frequency: 'daily',
    cronExpression: '0 4 * * *',
    time: '04:00 UTC',
    server: 'ap-southeast-1',
    database: 'marketplace_db',
    retentionDays: 30,
    enabled: false,
    lastRun: '2026-07-11T04:00:00Z',
    nextRun: '—',
    description: 'Backup diario de marketplace. DESHABILITADO por problemas de conectividad con servidor ap-southeast-1.',
  },
];

export const calendarEvents = backupSchedules
  .filter((s) => s.enabled)
  .map((s) => {
    const next = new Date(s.nextRun);
    return {
      id: s.id,
      title: s.name,
      date: next.toISOString().split('T')[0],
      time: s.time,
      database: s.database,
      server: s.server,
      frequency: s.frequency,
      scheduleId: s.id,
    };
  });